import { BALANCE } from '../balance';
import type { LetterDefInput, MailboxCycleInput, PostcardDefInput } from '../schemas/progressionDefs';

/**
 * Mailbox (content-schemas §6.1–6.2, economy-progression §2.4, story-dialogue §5–§6). Deterministic 7-day cycle
 * that advances per grant, not per calendar day. Coin amounts come from BALANCE.mailboxCoins.
 */
const coins = { kind: 'coins', amount: BALANCE.mailboxCoins } as const;
const miniBox = { kind: 'miniBox', productKinds: 2, unitsEach: 2 } as const;

export const MAILBOX_CYCLE = {
  cycleLength: 7,
  days: [
    { day: 1, entries: [{ kind: 'postcard' }] },
    { day: 2, entries: [coins] },
    { day: 3, entries: [miniBox] },
    { day: 4, entries: [{ kind: 'npcLetter', rotation: ['npc_lan', 'npc_bao', 'npc_mit'] }] },
    { day: 5, entries: [coins] },
    { day: 6, entries: [{ kind: 'sticker' }] },
    { day: 7, entries: [{ kind: 'postcard' }, miniBox] },
  ],
  postcardOrder: ['pc_1', 'pc_2', 'pc_3', 'pc_4', 'pc_5', 'pc_6', 'pc_7', 'pc_8', 'pc_9', 'pc_10'],
  stickerPool: ['stk_mail_1', 'stk_mail_2', 'stk_mail_3'],
  stickerExhaustedCoins: BALANCE.mailboxCoins,
  preChapter2Letter: {
    letterId: 'ltr_npc_lan_1',
    requiresFlag: 'flag_npc_met_npc_lan',
    fallbackCoins: BALANCE.mailboxCoins,
  },
  // Box 6 starter stock: what a mini box holds before any product is unlocked.
  fallbackProducts: ['prod_instant_noodle', 'prod_canned_coffee', 'prod_cookie_box'],
} as const satisfies MailboxCycleInput;

const postcard = (n: number): PostcardDefInput => ({
  id: `pc_${String(n)}`,
  order: n,
  titleKey: `postcard.${String(n)}.title`,
  bodyKey: `postcard.${String(n)}.body`,
  image: `postcard_${String(n)}`,
  // Replays after all 10 were received use a "reprint" variant (economy §2.4).
  repeatImage: `postcard_${String(n)}_repeat`,
  special: false,
  attachments: [],
});

/** Granny's 10 travel postcards + the chapter 5 special (outside the cycle, granted by unl_chapter_5). */
export const POSTCARDS: readonly PostcardDefInput[] = [
  ...Array.from({ length: 10 }, (_, i) => postcard(i + 1)),
  {
    id: 'pc_ch5_key',
    order: 99,
    // The card IS chapter 5's letter: opening it runs dlg_ch5; the album shows the letter's first page.
    titleKey: 'story.ch5.title',
    bodyKey: 'story.ch5.2',
    image: 'postcard_ch5_key',
    special: true,
    attachments: [{ type: 'flag', flag: 'flag_studio_key' }],
  },
];

const letter = (from: 'npc_lan' | 'npc_bao' | 'npc_mit', n: number): LetterDefInput => ({
  id: `ltr_${from}_${String(n)}`,
  from,
  order: n,
  bodyKey: `letter.${from}.${String(n)}`,
  // Only Cô Lan's first letter is written to be readable before meeting the neighbors (story-dialogue §6).
  requiresMet: !(from === 'npc_lan' && n === 1),
  attachments: [],
});

/** Neighbor letters (mailbox day 4), rotating Lan → Bảo → Mít; letters never carry gifts. */
export const LETTERS: readonly LetterDefInput[] = [
  ...[1, 2, 3].flatMap((n) => [letter('npc_lan', n), letter('npc_bao', n), letter('npc_mit', n)]),
  {
    id: 'ltr_neighborhood_lam',
    from: 'npc_lan',
    order: 4,
    bodyKey: 'neighborhood.lam.invite',
    requiresMet: true,
  },
  {
    id: 'ltr_neighborhood_elias',
    from: 'npc_bao',
    order: 4,
    bodyKey: 'neighborhood.elias.invite',
    requiresMet: true,
  },
];
